import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { defaultGroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { SignalType } from 'api/signal/types';
import { isEqual } from 'lodash';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { useCallback, useState, useMemo } from 'react';

interface SignalFilterParams {
  signalType?: SignalType;
  myAccountSignalType?: SignalType;
  selectedSignals: string[];
}

interface UseFilterStateReturn {
  filter: GroupedFilter;
  selectedSignals: string[];
  signalFilterParams: SignalFilterParams;
  updateFilter: (newFilter: GroupedFilter) => void;
  updateSelectedSignals: (signals: string[]) => void;
  updateSignalFilterParams: (params: SignalFilterParams) => void;
  resetFilter: () => void;
}

export const useFilterState = (
  initialFilter: GroupedFilter,
  initialSelectedSignals: string[] = [],
  initialSignalFilterParams: SignalFilterParams = { selectedSignals: [] },
): UseFilterStateReturn => {
  const [filter, setFilter] = useState<GroupedFilter>(initialFilter);
  const [selectedSignals, setSelectedSignals] = useState<string[]>(
    initialSelectedSignals,
  );

  const { data: signalConfigurations } = useGetAllSignalConfigurationsQuery();

  const getAllReasonsForSignal = useCallback(
    (signalId: string | number) => {
      const signal = signalConfigurations?.find(
        (s: any) => s.signalConfigurationId.toString() === signalId.toString(),
      );

      if (!signal) {
        return [];
      }

      const reasons =
        signal.fields.find(
          (f: any) =>
            f.metaData &&
            f.metaData.options &&
            Array.isArray(f.metaData.options),
        )?.metaData?.options || [];

      // Use indexing logic for reason options
      // const signalIndex =
      //   signalConfigurations?.findIndex(
      //     (s) => s.signalConfigurationId.toString() === signalId.toString(),
      //   ) || 0;

      // let startingValue = 1;

      // for (let i = 0; i < signalIndex; i++) {
      //   const prevSignal = signalConfigurations?.[i];

      //   if (prevSignal) {
      //     const prevReasons =
      //       prevSignal.fields.find(
      //         (f: any) =>
      //           f.metaData &&
      //           f.metaData.options &&
      //           Array.isArray(f.metaData.options),
      //       )?.metaData?.options || [];

      //     startingValue += prevReasons.length;
      //   }
      // }

      // return reasons.map((reason: any, index: number) => ({
      //   ...reason,
      //   value: startingValue + index,
      // }));
      return reasons;
    },
    [signalConfigurations],
  );

  const [signalFilterParams, setSignalFilterParams] =
    useState<SignalFilterParams>(initialSignalFilterParams);

  const syncSelectedSignalsFromFilter = useCallback(
    (newFilter: GroupedFilter) => {
      // console.log('syncSelectedSignalsFromFilter', newFilter);
      const signalConfigCriteria = newFilter?.groupCriteria?.find(
        (criteria) =>
          'field' in criteria.criteria &&
          criteria.criteria.field.fieldName === 'signalConfigurationId',
      );

      if (signalConfigCriteria && 'value' in signalConfigCriteria.criteria) {
        const signalConfigIds = signalConfigCriteria.criteria.value as (
          | string
          | number
        )[];

        const signalConfigIdsAsStrings = signalConfigIds.map((id) =>
          id.toString(),
        );

        const currentSelectedSignalsSorted = selectedSignals.sort();

        if (!isEqual(currentSelectedSignalsSorted, signalConfigIdsAsStrings)) {
          setSelectedSignals(signalConfigIdsAsStrings);
          setSignalFilterParams({ selectedSignals: signalConfigIdsAsStrings });
        }
      } else {
        if (selectedSignals.length > 0) {
          setSelectedSignals([]);
          setSignalFilterParams({ selectedSignals: [] });
        }
      }

      // Find the single consolidated reason criteria
      const reasonCriteria = newFilter?.groupCriteria?.find(
        (criteria) =>
          'field' in criteria.criteria &&
          criteria.criteria.field.fieldName === 'Reason',
      );

      if (reasonCriteria && 'value' in reasonCriteria.criteria) {
        const currentReasonValues = reasonCriteria.criteria.value as number[];
        const validReasonValues: number[] = [];

        const currentSelectedSignalIds =
          signalConfigCriteria && 'value' in signalConfigCriteria.criteria
            ? (signalConfigCriteria.criteria.value as (string | number)[]).map(
                (id) => id.toString(),
              )
            : [];

        // Validate reason values against selected signals
        currentReasonValues.forEach((reasonValue) => {
          let isValid = false;

          for (const signalId of currentSelectedSignalIds) {
            const signalReasons = getAllReasonsForSignal(signalId);
            const signalReasonValues = signalReasons.map(
              (reason: any) => reason.value,
            );

            if (signalReasonValues.includes(reasonValue)) {
              isValid = true;
              break;
            }
          }

          if (isValid) {
            validReasonValues.push(reasonValue);
          }
        });

        // Only update if there are invalid reason values
        if (validReasonValues.length !== currentReasonValues.length) {
          const updatedGroupCriteria = newFilter.groupCriteria.map(
            (criteria) => {
              if (
                'field' in criteria.criteria &&
                criteria.criteria.field.fieldName === 'Reason'
              ) {
                return {
                  ...criteria,
                  criteria: {
                    ...criteria.criteria,
                    value: validReasonValues,
                  } as any,
                };
              }

              return criteria;
            },
          );

          const updatedFilter = {
            ...newFilter,
            groupCriteria: updatedGroupCriteria,
          };

          setFilter(updatedFilter);
        }
      }
    },
    [selectedSignals, getAllReasonsForSignal],
  );

  const updateFilter = useCallback(
    (newFilter: GroupedFilter) => {
      setFilter(newFilter);
      syncSelectedSignalsFromFilter(newFilter);
    },
    [syncSelectedSignalsFromFilter],
  );

  const updateSelectedSignals = useCallback((newSelectedSignals: string[]) => {
    setSelectedSignals(newSelectedSignals);
  }, []);

  const updateSignalFilterParams = useCallback((params: SignalFilterParams) => {
    setSignalFilterParams(params);
  }, []);

  const resetFilter = useCallback(() => {
    setFilter(defaultGroupedFilter(FilterGroupType.Company));
    setSelectedSignals([]);
    setSignalFilterParams({ selectedSignals: [] });
  }, []);

  const returnValue = useMemo(
    () => ({
      filter,
      selectedSignals,
      signalFilterParams,
      updateFilter,
      updateSelectedSignals,
      updateSignalFilterParams,
      resetFilter,
    }),
    [
      filter,
      selectedSignals,
      signalFilterParams,
      updateFilter,
      updateSelectedSignals,
      updateSignalFilterParams,
      resetFilter,
    ],
  );

  return returnValue;
};
